import { Chain } from "thirdweb";
import { RPCError } from "./RPCError";
import { RPCMethods } from "./RPCMethods";
import { EthereumProvider } from "./EthereumProvider";
import { ChainUtils } from "../../utils/ChainUtils";
import { ObjectFieldsChecker } from "../../../tools/ObjectFieldsChecker";

export class RPCProvider {
    private readonly provider: EthereumProvider;
    private readonly rpcMethods: RPCMethods;

    public constructor(provider: EthereumProvider) {
        this.provider = provider;
        this.rpcMethods = new RPCMethods();
    }

    public async getAccounts(): Promise<Array<string>> {
        return await this.wrapRequest(this.provider.request({ method: this.rpcMethods.getAccounts })) as Array<string> ?? [];
    }

    public async requestAccounts(): Promise<Array<string>> {
        return await this.wrapRequest(this.provider.request({ method: this.rpcMethods.requestAccounts })) as Array<string> ?? [];
    }

    public async getChainId(): Promise<string> {
        const chainId: string | number = await this.wrapRequest(this.provider.request({ method: this.rpcMethods.getChainId })) as string | number ?? null;
        return chainId == null ? null : typeof chainId === "string" ? chainId : ChainUtils.idToHex(chainId);
    }

    public async addChain(chain: Chain): Promise<void> {
        await this.wrapRequest(this.provider.request({
            method: this.rpcMethods.addChain,
            params: [
                {
                    chainId: ChainUtils.idToHex(chain.id),
                    chainName: chain.name,
                    rpcUrls: [
                        chain.rpc
                    ],
                    nativeCurrency: chain.nativeCurrency,
                    blockExplorerUrls: chain.blockExplorers.map((value) => value.url)
                }
            ]
        }));
    }

    public async switchChain(chainId: string | number): Promise<void> {
        const chain = typeof chainId === "string" ? chainId : ChainUtils.idToHex(chainId);
        await this.wrapRequest(this.provider.request({
            method: this.rpcMethods.switchChain,
            params: [
                {
                    chainId: chain
                }
            ]
        }));
    }

    public async signMessage(nonce: string, address: string): Promise<string> {
        const hexNonce = `0x${Buffer.from(nonce, "utf8").toString("hex")}`;
        return await this.wrapRequest(this.provider.request({
            method: this.rpcMethods.signMessage,
            params: [
                hexNonce,
                address
            ]
        })) as string ?? null;
    }

    public async sendTransaction(toAddress: string, fromAddress: string, data: string): Promise<string> {
        return await this.wrapRequest(this.provider.request({
            method: this.rpcMethods.sendTransaction,
            params: [
                {
                    to: toAddress,
                    from: fromAddress,
                    data
                }
            ]
        })) as string ?? null;
    }

    public async revokePermissions(): Promise<void> {
        await this.wrapRequest(this.provider.request({ method: this.rpcMethods.revokePermissions }));
    }

    private async wrapRequest(request: Promise<unknown>): Promise<unknown> {
        try {
            return await request;
        }
        catch (e: unknown) {
            const rpcError = new RPCError(e);
            if (ObjectFieldsChecker.checkFields(rpcError, ["code", "message"])) {
                throw rpcError;
            }
            throw e;
        }
    }
}